import { useCallback, useEffect, useRef, useState } from 'react';
import { api, ApiError } from './api';

/** Minimal data-fetching hook (no external query library). Refetches when `path`/`query` change. */
export function useApi<T = any>(path: string | null, query?: Record<string, any>, deps: unknown[] = []) {
  const [data, setData] = useState<T | undefined>(undefined);
  const [error, setError] = useState<ApiError | null>(null);
  const [loading, setLoading] = useState<boolean>(!!path);
  const key = path ? path + JSON.stringify(query ?? {}) : null;
  const ctl = useRef<AbortController | null>(null);

  const load = useCallback(async (silent = false) => {
    if (!path) return;
    ctl.current?.abort();
    const c = new AbortController();
    ctl.current = c;
    if (!silent) setLoading(true);
    try {
      const d = await api<T>(path, { query, signal: c.signal });
      if (!c.signal.aborted) { setData(d); setError(null); }
    } catch (e: any) {
      if (e?.name !== 'AbortError') setError(e instanceof ApiError ? e : new ApiError(0, 'error', String(e)));
    } finally {
      if (!c.signal.aborted) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, ...deps]);

  useEffect(() => { load(); return () => ctl.current?.abort(); }, [load]);
  return { data, error, loading, reload: () => load(true), setData };
}

/** Wraps an async action with pending/error state. */
export function useAction<A extends any[], R>(fn: (...a: A) => Promise<R>) {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const run = useCallback(async (...a: A): Promise<R | undefined> => {
    setPending(true); setError(null);
    try { return await fn(...a); }
    catch (e: any) { setError(e instanceof ApiError ? e : new ApiError(0, 'error', String(e?.message ?? e))); return undefined; }
    finally { setPending(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fn]);
  return { run, pending, error, setError };
}

export function useDebounced<T>(value: T, ms = 300): T {
  const [v, setV] = useState(value);
  useEffect(() => { const t = setTimeout(() => setV(value), ms); return () => clearTimeout(t); }, [value, ms]);
  return v;
}

export function useLocalStorage<T>(key: string, initial: T): [T, (v: T) => void] {
  const [v, setV] = useState<T>(() => {
    try { const s = localStorage.getItem(key); return s ? (JSON.parse(s) as T) : initial; } catch { return initial; }
  });
  const set = useCallback((nv: T) => { setV(nv); try { localStorage.setItem(key, JSON.stringify(nv)); } catch { /* storage unavailable */ } }, [key]);
  return [v, set];
}
